import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthShell from './components/AuthShell';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import NativeBridgeDemo from './components/NativeBridgeDemo';
import HomePage from './pages/HomePage';
import { TravelModule, CreateTravel } from './modules/travel';
import { ChatModule } from './modules/chat';
import TripDetailPage from './pages/TripDetailPage';
import TravelCreatePage from './pages/TravelCreatePage';
import CardDetailPage from './pages/CardDetailPage';
import CardListPage from './pages/CardListPage';
import TravelPostDetailPage from './pages/TravelPostDetailPage';
import TravelPostPreviewPage from './pages/TravelPostPreviewPage';
import SearchPage from './pages/SearchPage';
import GuideIndexPage from './pages/GuideIndexPage';
import UserProfilePage from './pages/UserProfilePage';
import CityPage from './pages/CityPage';
import CityIndexPage from './pages/CityIndexPage';
import DownloadPage from './pages/DownloadPage';
import TravelShareTargetPage from './pages/TravelShareTargetPage';
import SettingsPage from './pages/SettingsPage';
import LanguageSettingsPage from './pages/LanguageSettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import { TodoListModule } from './modules/todo';
import ProfileModule from './modules/profile';
import NotificationsModule from './modules/notifications';
import FriendsModule from './modules/friends';
import type { AppDispatch, RootState } from './store';
import { getCurrentUser } from '@cubemeet/core/store/slices/authSlice';
import { supabase } from '@cubemeet/core/services/supabase';

const App: React.FC = () => {
  const [authReady, setAuthReady] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const isAuthenticated = Boolean(authState?.isAuthenticated);

  useEffect(() => {
    let cancelled = false;
    let readyTimeout: number | undefined;

    if (typeof window !== 'undefined') {
      readyTimeout = window.setTimeout(() => {
        if (!cancelled) {
          console.warn('Auth bootstrap timed out, forcing ready state');
          setAuthReady(true);
        }
      }, 8000);
    }

    const persistPostAuthRedirect = (hint: string | null) => {
      if (typeof window === 'undefined' || !hint) return;
      const sanitized = hint.replace(/^#+/, '').trim();
      if (!sanitized) return;

      const [rawPath] = sanitized.split('?');
      const path = rawPath ? (rawPath.startsWith('/') ? rawPath : `/${rawPath}`) : '/';
      const hash = `#${sanitized}`;

      try {
        window.sessionStorage.setItem(
          'cubemeet:postAuthRedirect',
          JSON.stringify({ path, hash })
        );
      } catch (err) {
        console.warn('Failed to cache post-auth redirect hint', err);
      }
    };

    const consumeOAuthHashSession = async () => {
      if (typeof window === 'undefined') return;
      const { hash, pathname, search } = window.location;
      if (!hash || !hash.includes('access_token=')) return;

      const segments = hash.split('#').filter(Boolean);
      const tokenIndex = segments.findIndex((segment) => segment.includes('access_token='));
      if (tokenIndex === -1) return;

      const tokenSegment = segments[tokenIndex];
      const redirectHint = tokenIndex > 0 ? segments[tokenIndex - 1] : null;
      const params = new URLSearchParams(tokenSegment.replace(/^\?/, ''));
      const accessToken = params.get('access_token') || undefined;
      const refreshToken = params.get('refresh_token') || undefined;

      if (!accessToken || !refreshToken) {
        console.warn('Detected OAuth hash but tokens are incomplete');
      } else {
        try {
          console.log('🔐 Restoring Supabase session from URL hash');
          const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) {
            console.error('Failed to set Supabase session from hash', error);
          } else {
            console.log('✅ Supabase session restored from OAuth hash');
            persistPostAuthRedirect(redirectHint);
          }
        } catch (err) {
          console.error('Error while restoring session from hash', err);
        }
      }

      try {
        const cleanHash = redirectHint ? `#${redirectHint.replace(/^#+/, '')}` : '';
        const cleanUrl = `${pathname}${search}${cleanHash}`;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (err) {
        console.warn('Failed to clean OAuth hash from URL', err);
      }
    };

    const restoreLegacySession = async () => {
      if (typeof window === 'undefined') return;
      const hasLegacyToken = !!window.localStorage.getItem('supabase_token');
      if (!hasLegacyToken) return;

      const token = window.localStorage.getItem('supabase_token');
      const refresh = window.localStorage.getItem('supabase_refresh') || null;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session && token) {
          console.log('🛠 Restoring legacy supabase session from localStorage');
          await supabase.auth.setSession({ access_token: token, refresh_token: refresh });
        }
      } catch (e) {
        if (token) {
          try {
            await supabase.auth.setSession({ access_token: token, refresh_token: refresh });
          } catch (err) {
            console.warn('Failed to restore legacy session directly', err);
          }
        }
      }
    };

    (async () => {
      try {
        await consumeOAuthHashSession();
        await restoreLegacySession();
      } catch (err) {
        console.warn('Auth bootstrap encountered an error', err);
      } finally {
        try {
          await dispatch(getCurrentUser());
        } catch (err) {
          // ignore: still mark auth as ready
        } finally {
          if (readyTimeout) {
            clearTimeout(readyTimeout);
            readyTimeout = undefined;
          }
          if (!cancelled) setAuthReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (readyTimeout) {
        clearTimeout(readyTimeout);
      }
    };
  }, [dispatch]);

  // ensure legacy body class removed
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('app-shell-mode');
  }, []);

  if (!authReady) {
    // while we perform initial auth bootstrap we used to block all routes with
    // a global loading UI. That causes share/public pages (e.g. /card/:id)
    // to show "正在檢查登入狀態…" unnecessarily. Instead only block when
    // we are on a protected/interactive route. Public sharing routes should
    // render immediately and let the auth bootstrap finish in background.
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    // Public routes that should render before auth bootstrap completes.
    // Note: travel/trip routes require authentication; remove them from
    // the public list so we wait for the global auth bootstrap and avoid
    // flashing the auth page on refresh.
    const publicPrefixes = [
      '/', '/card/', '/cards', '/travel-post/', '/city', '/user', '/download', '/pwa-status', '/share/', '/auth'
    ];
    const isPublicRoute = publicPrefixes.some(p => pathname === p || pathname.startsWith(p));

    if (!isPublicRoute) {
      return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
          <div style={{ textAlign: 'center' }}>
            <svg width="56" height="56" viewBox="0 0 50 50" aria-hidden="true">
              <g>
                <circle cx="25" cy="25" r="20" stroke="#FF6B6B" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="31.4 31.4" />
                <animateTransform attributeName="transform" attributeType="XML" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
              </g>
            </svg>
            <div style={{ marginTop: 12, color: '#333', fontSize: 16 }}>正在檢查登入狀態…</div>
          </div>
        </div>
      );
    }
    // else: fallthrough and render router so public/share pages are accessible
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Routes WITHOUT AppLayout (Full screen or different layout) */}
        <Route path="/trip/:groupId" element={<TripDetailPage />} />
        <Route path="/travel-post/preview/:id" element={<TravelPostPreviewPage />} />
        <Route path="/share/travel" element={<TravelShareTargetPage />} />
        <Route path="/travel_post/guide" element={<GuideIndexPage />} />
        <Route path="/download" element={<DownloadPage />} />
        
        <Route path="/auth" element={<AuthShell />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* Routes WITH AppLayout (Persistent Bottom Nav) */}
        {/* Chat should be full-screen without the bottom navigation on web —
          render it as a top-level protected route so AppLayout (and BottomNav)
          are not mounted for /chat/* pages. */}
        <Route path="/chat/*" element={<ProtectedRoute><ChatModule /></ProtectedRoute>} />

        <Route element={<AppLayout showBottomNav />}>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/card/:id" element={<CardDetailPage isModal={false} />} />
            <Route path="/cards" element={<CardListPage />} />
            <Route path="/travel/*" element={<TravelModule />} />
            <Route path="/city" element={<CityIndexPage />} />
            <Route path="/city/:cityKey" element={<CityPage />} />
            <Route path="/travel-post/:id" element={<TravelPostDetailPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/user/:id" element={<UserProfilePage />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/language" element={<LanguageSettingsPage />} />
                <Route path="/settings/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/settings/terms-of-service" element={<TermsOfServicePage />} />
                
                <Route path="/travel/create" element={<CreateTravel onBack={() => window.history.back()} />} />
                <Route path="/my-travels/*" element={<TravelModule />} />
                
                {/* Chat moved to top-level (no AppLayout) */}
                <Route path="/todo/*" element={<TodoListModule />} />
                <Route path="/profile/*" element={<ProfileModule />} />
                <Route path="/notifications/*" element={<NotificationsModule />} />
                <Route path="/friends/*" element={<FriendsModule />} />
            </Route>
        </Route>

        {/* Catch-all for unknown routes - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
